import React from 'react';

// Small, accessible foundation components (spec section 26). Deliberately
// plain -- this is the token/semantics-sharing layer, not a large component
// library. Each element has a real accessible name/role and a 44px minimum
// touch target (spec section 29).

export function Button({
  children,
  onClick,
  disabled,
  variant = 'primary',
  type = 'button',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type}
      className={`ozira-button${variant === 'secondary' ? ' ozira-button--secondary' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export function Input({
  label,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  const id = React.useId();
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', marginBottom: 4, fontSize: 'var(--ozira-font-size-sm)' }}>
        {label}
      </label>
      <input id={id} className="ozira-input" {...props} />
    </div>
  );
}

export function Card({ children }: { children: React.ReactNode }) {
  return <div className="ozira-card">{children}</div>;
}

export function Badge({ children }: { children: React.ReactNode }) {
  return <span className="ozira-badge">{children}</span>;
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span className="ozira-loading" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <div className="ozira-empty-state">
      <strong>{title}</strong>
      {description ? <span>{description}</span> : null}
    </div>
  );
}

export function ErrorState({ title, description }: { title: string; description?: string }) {
  return (
    <div className="ozira-error-state" role="alert">
      <strong>{title}</strong>
      {description ? <span>{description}</span> : null}
    </div>
  );
}

export function Avatar({ label }: { label: string }) {
  const initial = label.trim().charAt(0).toUpperCase() || '?';
  return (
    <div className="ozira-avatar" aria-hidden="true">
      {initial}
    </div>
  );
}
